// Side view of the anatomical body map (MUSCLE_BODY_MAP_PLAN.md, Task 5.1).
// React-free / JSX-free -- scripts/validateMuscleMap.ts imports this module
// directly (hazard #7).
//
// The SHARED SKELETON SPEC lives in the header comment of regionsFront.ts and
// applies verbatim here (hazard #6): same 220x520 canvas, same y landmarks, so
// the figure never jumps when the view switch is toggled -- crown 10, chin 72,
// neck base 88, acromion 100, armpit 130, nipple 140, xiphoid 172, navel 205,
// iliac crest 232, pubis 260, trochanter 270, mid-thigh 310, knee 364,
// mid-calf 418, ankle 481, sole 511.
//
// ---------------------------------------------------------------------------
// WHICH WAY THE FIGURE FACES
// ---------------------------------------------------------------------------
// The figure faces screen-LEFT, so anterior is low x and posterior is high x.
// That is also what makes the "-l" region suffix correct: a person facing your
// left shows you their LEFT flank, which is the near side this view draws
// (D-BM-8 / validator rule R4's side-view allowance).
//
// ---------------------------------------------------------------------------
// SIDE PROFILE
// ---------------------------------------------------------------------------
//   y   | anterior x | posterior x | depth | note
//  -----|------------|-------------|-------|--------------------------------
//    88 |     95     |     131     |  36   | neck base
//   100 |     88     |     153     |  65   | acromion / top of the deltoid
//   132 |     83     |     157     |  74   | chest, the deepest section
//   172 |     86     |     152     |  66   | xiphoid
//   205 |     89     |     147     |  58   | navel; the waist is shallowest
//   232 |     91     |     157     |  66   | iliac crest / top of the buttock
//   250 |     93     |     163     |  70   | the buttock's own widest point
//   260 |     96     |     163     |  67   | pubis in front, buttock behind
//   310 |     89     |     144     |  55   | mid-thigh
//   364 |     92     |     136     |  44   | knee
//   418 |     99     |     150     |  51   | mid-calf, gastrocnemius bulge
//   481 |    104     |     133     |  29   | ankle / achilles
//   511 |     72     |     143     |  71   | sole: toes forward, heel back
//
// A lateral chest is genuinely about 0.8x its own breadth (the front view's
// chest is 86 across at the armpit), and the S-curve down the back edge --
// thoracic kyphosis out at 157, lumbar lordosis in at 147, buttock out again
// at 163 -- is the single thing that most makes a profile read as a body
// instead of a plank. The upper back is drawn deep on purpose: the posterior
// strip the hanging arm leaves has to stay wide enough to tap.
//
// ---------------------------------------------------------------------------
// WHY THE ARM OVERLAPS THE TORSO
// ---------------------------------------------------------------------------
// The arm hangs from a joint in the middle of the torso's depth, so in a true
// lateral projection it ALWAYS covers the middle of the flank -- no amount of
// swinging it forward or back clears the ribcage without turning the pose into
// a reach. So SIDE_ARM is its own silhouette path drawn over the trunk, and
// the trunk's regions live in the two strips it leaves:
//
//   anterior strip  x 83-106  pecs, serratus, rectus, obliques, TFL
//   arm             x 97-144  the only place any view separates all three
//                             deltoid heads and both triceps heads
//   posterior strip x 133-163 teres, lat, erectors, glutes
//
// Those strips are 15-20 units wide, which is about how much of each of those
// muscles a lateral view actually shows.
//
// Unlike the front and back views nothing here is mirrored: this view draws
// the near side only, so all 35 regions are unpaired and suffixed "-l".
//
// The taxonomy expansion (EXERCISE_LIBRARY_AND_MUSCLE_TAXONOMY_PLAN.md Task
// 1.4) added the neck, the sartorius and the lower-leg compartments. Two
// existing paths were re-cut to make room rather than being drawn over: the
// tibialis anterior gave up its posterior third to the peroneals, and the
// gastrocnemius gave up 4 units of its anterior border to the tibialis
// posterior. Both were wider than a lateral projection of them really is.
//
// Task 2.3 added two more and re-cut two more for them. The upper trapezius'
// anterior border was lifted so the posterior triangle -- the wedge between
// the trapezius, the scalenes and the deltoid cap where the levator scapulae
// is the one thing you can actually see -- opens up; and the gluteus medius
// was split down its own length, the anterior half becoming the gluteus
// minimus, which is where the minimus is: deep to the medius and in front of
// it. Neither muscle got a floating region, because this view has nowhere to
// float one.

import type { AnatomicalMuscle } from "@medassist/shared";
import type { BodyRegion } from "./geometry.js";
import { VIEWBOX } from "./geometry.js";

export const SIDE_VIEWBOX = VIEWBOX;

/** Builds the single near-side region for a muscle in this view. */
function nearRegion(muscle: AnatomicalMuscle, d: string): BodyRegion {
  return { id: `${muscle}-l`, muscle, side: "left", d };
}

/**
 * Trunk, neck and legs, traced down the anterior edge from the base of the
 * neck to the toes, along the sole, then back up the posterior edge to the
 * nape. The head and the near arm are separate paths because both overlap it.
 */
const SIDE_BODY = [
  "M 100 68",
  "C 98 76 96 82 95 88", // front of the neck into the top of the chest
  "C 92 92 88 96 87 104", // clavicle and the upper border of the pec
  "C 84 114 83 124 83 134", // pectoral mass -- the deepest part of the chest
  "C 83 148 84 160 86 172", // chest wall down to the xiphoid
  "C 87 184 88 195 89 205", // upper abdomen to the navel
  "C 90 216 90 224 91 232", // lower abdomen to the iliac crest
  "C 92 244 93 252 96 260", // front of the hip down to the pubis
  "C 95 268 93 276 92 284", // hip crease into the front of the thigh
  "C 90 296 89 306 89 316", // mid-thigh
  "C 90 334 91 350 92 360", // thigh down to the knee
  "C 93 368 94 374 95 380", // patella
  "C 97 392 98 404 99 416", // tibial crest past mid-calf
  "C 101 440 103 462 104 480", // shin down to the ankle
  "C 101 490 94 499 86 504", // instep
  "C 82 506 79 508 77 507", // ball of the foot
  "C 72 508 71 513 77 513", // big toe, the one a profile really shows
  "C 84 513 90 512 94 511", // the smaller toes tucked behind it
  "L 143 511", // sole
  "C 147 508 148 499 144 494", // heel
  "C 138 488 134 483 133 478", // achilles up to the ankle
  "C 140 460 149 438 150 416", // gastrocnemius bulge
  "C 149 396 143 378 138 368", // below the knee
  "C 136 364 135 360 135 356", // back of the knee
  "C 138 340 141 322 145 306", // hamstrings
  "C 151 292 158 280 162 268", // gluteal fold into the buttock
  "C 165 256 163 244 158 234", // buttock up to the iliac crest
  "C 153 226 148 218 147 208", // lumbar lordosis -- the curve into the waist
  "C 146 194 150 176 153 162", // back up the thoracic wall
  "C 155 152 157 142 157 132", // thoracic kyphosis, deepest part of the back
  "C 157 120 156 110 153 100", // top of the shoulder
  "C 149 90 140 80 132 72", // trapezius slope up to the nape
  "C 127 68 116 66 108 67", // across the top of the neck (hidden by the head)
  "C 104 67 101 67 100 68",
  "Z",
].join(" ");

/**
 * Head in profile: the same smooth ovoid the front and back views use, with
 * only as much brow, nose and chin as it takes to say which way the figure
 * faces. Overstate the face and the eye goes to it instead of to the muscles;
 * leave it out entirely and a lateral figure reads as a ball on a neck.
 */
const SIDE_HEAD = [
  "M 112 10", // crown, at the same y as the front and back views
  "C 99 10 89 21 88 33", // forehead
  "C 87 40 85 44 83 47", // brow ridge into the bridge of the nose
  "C 81 50 82 55 86 57", // nose
  "C 88 64 91 71 98 75", // upper lip into the chin
  "C 105 78 113 77 120 73", // jawline
  "C 128 69 134 61 137 51", // angle of the jaw
  "C 139 41 138 26 130 17", // occiput
  "C 125 12 119 10 112 10",
  "Z",
].join(" ");

// No ear path: in profile the ear sits entirely inside the skull's silhouette,
// so against a single-color outline fill it would render as nothing at all.

/**
 * The near arm, hanging relaxed with the hand open: deltoid cap, upper arm,
 * forearm, then the hand seen edge-on -- thumb along the front, the index
 * finger's own contour, and the remaining three read as one mass behind it,
 * which is all a true lateral projection of a hand ever shows.
 *
 * In that projection the arm never breaks the trunk's outline -- it hangs
 * inside the torso's own depth -- so this path is drawn over the trunk in the
 * same silhouette color, and the arm reads as a limb because its own muscle
 * regions tile it. It is set slightly forward of the torso's mid-depth so the
 * anterior and posterior strips come out about equally wide; anatomically the
 * humerus does sit forward of the spine.
 */
const SIDE_ARM = [
  "M 100 104",
  "C 97 114 97 126 99 138", // front of the deltoid into the upper arm
  "C 100 152 101 166 102 180", // biceps line down to the elbow
  "C 103 194 104 206 105 218", // front of the forearm
  "C 106 234 107 248 109 260", // down to the wrist
  "C 106 267 103 275 104 283", // thumb, down the front of the hand
  "C 106 288 110 287 110 282", // thumb tip, back up into the web
  "C 108 289 107 297 108 303", // index finger, front edge
  "C 110 309 114 309 116 304", // index fingertip
  "C 117 299 117 295 117 291", // index finger, back edge
  "C 119 297 121 305 125 304", // the remaining three fingers, seen edge-on
  "C 128 302 129 296 129 290",
  "C 130 280 130 270 129 262", // back of the hand up to the wrist
  "C 131 246 133 230 134 214", // back of the forearm
  "C 135 202 136 194 136 186", // elbow
  "C 138 170 142 152 144 136", // triceps line
  "C 146 124 146 112 144 102", // back of the deltoid
  "C 138 94 126 91 115 94", // over the top of the shoulder
  "C 107 96 102 99 100 104",
  "Z",
].join(" ");

export const SIDE_OUTLINE: string[] = [SIDE_BODY, SIDE_HEAD, SIDE_ARM];

/**
 * Every region is sized to fill its strip edge to edge rather than to float in
 * the middle of it. A lateral view has none of the room the front and back
 * views can afford to give away: leave gaps and the trunk reads as confetti
 * instead of anatomy, and the arm stops reading as an arm at all.
 */
export const SIDE_REGIONS: BodyRegion[] = [
  // --- neck ------------------------------------------------------------------
  // Scalenes: deep, sitting behind the SCM in the lower neck on their way down
  // to the first rib, which is the only wedge of them a profile shows. Kept
  // forward of x 121 -- the trapezius' anterior border comes down to 117 at
  // the nape, and the deltoid cap starts at y 92.
  nearRegion(
    "scalenes",
    "M 117 82 C 114 86 112 90 111 93 C 112 95 115 95 117 92 C 119 89 120 86 120 83 C 120 80 118 79 117 82 Z",
  ),
  // This is the view the sternocleidomastoid is FOR: front-on it is a strap,
  // in profile it is the whole diagonal of the neck, running from the mastoid
  // behind the ear down and forward to the sternum. Listed before the
  // trapezius and the deltoid so either can paint over the seam it shares
  // with them.
  nearRegion(
    "sternocleidomastoid",
    "M 114 76 C 109 83 103 89 97 93 C 96 97 99 99 102 97 C 109 92 115 86 118 79 C 119 75 116 73 114 76 Z",
  ),

  // Levator scapulae: the strap running down the back of the neck to the top
  // corner of the shoulder blade. In profile it fills the posterior triangle --
  // the wedge between the trapezius above it, the scalenes in front of it, and
  // the deltoid cap below -- which is the only stretch of it any view shows
  // uncovered. Listed before the trapezius so the trapezius takes the seam.
  nearRegion(
    "levator-scapulae",
    "M 124 81 C 127 84 130 88 132 92 C 132 95 129 95 127 93 C 125 90 122 87 120 84 C 118 81 122 78 124 81 Z",
  ),

  // --- shoulder girdle -------------------------------------------------------
  // Upper trapezius. This is the view that shows what the other two only imply:
  // it originates on the occipital bone, so the band runs UP the back of the
  // neck before turning down the slope to the acromion. Its anterior border was
  // lifted (it now leaves the nape at y 76 rather than y 81) to open the
  // posterior triangle the levator above occupies; the band is still 11 deep at
  // the neck, which is as much of it as a profile shows anyway.
  nearRegion(
    "traps-upper",
    "M 122 68 C 132 74 143 87 150 101 C 152 108 147 113 142 108 C 137 92 129 82 120 76 C 118 71 118 66 122 68 Z",
  ),
  // The deltoid cap splits into three vertical bands -- the whole point of this
  // view, and the only place any view separates the anterior and posterior
  // heads. Adjacent borders are exact reverses of one another, so the three
  // heads tile the cap with no gap and no overlap, all converging on the
  // deltoid tuberosity at half the humerus.
  nearRegion(
    "delt-anterior",
    "M 115 94 C 107 96 102 99 100 104 C 97 114 97 127 99 139 C 104 143 110 143 114 139 C 113 124 113 107 115 94 Z",
  ),
  nearRegion(
    "delt-lateral",
    "M 114 139 C 113 124 113 107 115 94 C 122 92 128 93 132 96 C 129 110 128 124 129 140 C 124 144 118 144 114 139 Z",
  ),
  nearRegion(
    "delt-posterior",
    "M 129 140 C 128 124 129 110 132 96 C 138 99 143 106 144 116 C 145 126 144 134 142 141 C 138 144 133 144 129 140 Z",
  ),

  // --- chest wall (anterior strip) ------------------------------------------
  // Seen edge-on the pec is a wedge: deep at the sternum in front, thinning as
  // its fibers converge back toward the humeral insertion under the arm.
  nearRegion(
    "pec-clavicular",
    "M 96 100 C 90 103 86 111 85 121 C 84 128 84 133 85 138 C 90 139 96 136 99 130 C 98 118 97 108 96 100 Z",
  ),
  nearRegion(
    "pec-sternal",
    "M 85 140 C 84 150 84 160 86 170 C 88 177 91 180 94 179 C 97 170 100 158 101 146 C 101 142 101 140 101 138 C 96 141 89 142 85 140 Z",
  ),
  // Serratus: the digitations on the lower ribs, in the wedge the pec's
  // posterior-inferior border and the hanging arm leave open.
  nearRegion(
    "serratus-anterior",
    "M 94 176 C 98 178 101 182 102 187 C 99 190 95 189 94 185 C 92 181 92 178 94 176 Z " +
      "M 96 191 C 100 193 103 197 104 202 C 101 205 97 204 96 200 C 94 196 94 193 96 191 Z",
  ),

  // --- abdominal wall --------------------------------------------------------
  // Rectus is the narrow anterior strip; the obliques are the flank behind it,
  // ending on the iliac crest the way the real muscle does.
  nearRegion(
    "rectus-abdominis",
    "M 90 176 C 88 187 88 200 89 214 C 90 229 92 242 95 252 C 99 249 100 236 99 219 C 97 202 94 187 93 178 C 92 176 91 175 90 176 Z",
  ),
  nearRegion(
    "obliques",
    "M 98 190 C 102 197 105 209 106 222 C 106 233 104 241 101 245 C 97 239 95 223 95 209 C 96 197 97 192 98 190 Z",
  ),

  // --- arm: the six regions tile the limb so it reads as one -----------------
  nearRegion(
    "biceps-brachii",
    "M 113 142 C 106 148 102 160 102 173 C 102 183 105 190 109 192 C 113 186 115 172 116 159 C 116 149 114 143 113 142 Z",
  ),
  nearRegion(
    "brachialis",
    "M 109 194 C 106 199 105 205 106 210 C 108 214 111 215 113 212 C 113 205 112 198 109 194 Z",
  ),
  nearRegion(
    "triceps-lateral",
    "M 127 145 C 122 153 119 166 120 179 C 121 189 124 194 127 193 C 130 184 132 168 131 155 C 130 147 129 145 127 145 Z",
  ),
  nearRegion(
    "triceps-long",
    "M 139 143 C 142 152 143 165 141 178 C 139 188 136 194 133 192 C 132 182 133 167 135 155 C 136 147 137 143 139 143 Z",
  ),
  nearRegion(
    "forearm-flexors",
    "M 114 214 C 109 222 106 234 106 246 C 106 255 109 261 113 262 C 117 256 118 241 117 228 C 117 218 116 214 114 214 Z",
  ),
  nearRegion(
    "forearm-extensors",
    "M 132 198 C 135 208 136 221 135 235 C 134 246 131 254 128 255 C 125 246 125 230 127 215 C 128 204 130 198 132 198 Z",
  ),

  // --- back (posterior strip) ------------------------------------------------
  // Teres major and the lat's upper border are the back wall of the armpit --
  // the one place any view shows the lat inserting under the arm. Below it the
  // lat widens down the flank, which is the profile of the same triangle the
  // back view draws head-on.
  nearRegion(
    "teres-major",
    "M 148 140 C 153 145 155 154 154 163 C 153 170 149 173 146 171 C 143 163 142 149 144 141 C 145 139 147 139 148 140 Z",
  ),
  nearRegion(
    "lat-dorsi",
    "M 145 170 C 150 179 153 191 152 204 C 151 214 147 221 143 219 C 139 208 137 192 139 179 C 140 173 142 169 145 170 Z",
  ),
  nearRegion(
    "erector-spinae",
    "M 141 218 C 145 224 148 232 149 241 C 149 248 147 253 143 252 C 138 245 136 233 136 223 C 136 218 138 217 141 218 Z",
  ),

  // --- hip -------------------------------------------------------------------
  nearRegion(
    "tfl",
    "M 103 246 C 99 253 97 262 98 270 C 100 277 105 279 108 276 C 110 266 109 253 105 246 C 104 244 104 244 103 246 Z",
  ),
  // The medius region split down its length: the anterior half is the minimus,
  // which lies deep to the medius and further forward on the ilium. Minimus
  // first so the medius takes the shared seam.
  nearRegion(
    "glute-minimus",
    "M 140 234 C 136 234 133 239 134 246 C 136 256 142 266 150 270 C 146 268 143 263 141 257 C 140 250 140 241 140 234 Z",
  ),
  nearRegion(
    "glute-medius",
    "M 140 234 C 140 241 140 250 141 257 C 143 263 146 268 150 270 C 158 270 163 261 161 252 C 157 242 149 234 140 234 Z",
  ),
  nearRegion(
    "glute-maximus",
    "M 148 268 C 156 269 160 277 159 287 C 157 297 149 304 140 306 C 133 307 129 301 130 292 C 132 279 139 268 148 268 Z",
  ),

  // --- thigh: three bands, anterior to posterior -----------------------------
  nearRegion(
    "rectus-femoris",
    "M 103 266 C 95 276 91 296 90 317 C 90 339 93 355 97 366 C 103 363 106 346 106 324 C 106 300 105 279 103 266 Z",
  ),
  nearRegion(
    "vastus-lateralis",
    "M 119 268 C 112 278 108 297 108 318 C 108 340 111 356 116 366 C 121 363 123 346 123 324 C 123 300 121 279 119 268 Z",
  ),
  nearRegion(
    "biceps-femoris",
    "M 136 288 C 140 297 141 312 140 329 C 139 345 136 358 131 364 C 126 359 124 344 125 325 C 127 306 131 293 136 288 Z",
  ),
  // Sartorius: it leaves the ASIS at the front of the hip and dives medially
  // across the thigh, so a lateral view catches only its first stretch --
  // the strap riding the anterior border of the thigh before it disappears
  // around the inside of the leg. Listed after the thigh bands because it is
  // superficial to every one of them.
  nearRegion(
    "sartorius",
    "M 97 249 C 95 266 92 286 91 306 C 91 316 92 326 93 332 C 96 333 98 330 97 324 C 96 310 96 292 98 272 C 99 262 100 254 101 250 C 100 247 98 246 97 249 Z",
  ),

  // --- lower leg: the gastrocnemius sitting on top of the soleus --------------
  nearRegion(
    "tibialis-anterior",
    "M 108 374 C 103 386 101 406 102 429 C 103 452 106 469 111 478 C 114 473 114 453 113 430 C 112 405 110 385 108 374 Z",
  ),
  // Peroneals: the lateral compartment, and this is the view that shows why
  // it is a compartment of its own -- the column between the shin in front of
  // it and the calf behind it, running down to the tendons that hook around
  // the lateral malleolus.
  nearRegion(
    "peroneals",
    "M 114 386 C 112 400 111 416 112 434 C 113 444 114 452 115 458 C 118 458 119 452 118 446 C 116 434 116 420 117 404 C 118 396 119 390 120 387 C 118 383 115 383 114 386 Z",
  ),
  // Tibialis posterior: the deepest muscle in the lower leg, between the
  // lateral compartment and the calf. It stops above the ankle here -- below
  // that its tendon runs down the far side of the leg, out of this view.
  nearRegion(
    "tibialis-posterior",
    "M 119 390 C 117 404 116 420 117 434 C 118 442 119 447 120 450 C 123 450 124 445 123 437 C 121 426 121 412 122 400 C 123 395 124 392 124 391 C 123 388 120 387 119 390 Z",
  ),
  nearRegion(
    "gastrocnemius",
    "M 130 372 C 139 379 146 396 147 415 C 146 432 140 446 131 450 C 126 445 122 429 122 409 C 123 390 126 377 130 372 Z",
  ),
  nearRegion(
    "soleus",
    "M 123 452 C 130 454 134 461 134 469 C 133 476 129 481 124 482 C 118 480 114 472 114 464 C 114 456 118 453 123 452 Z",
  ),
  // Plantar intrinsics: the arch, which is the one thing a profile of a foot
  // shows better than any other view. It fills the band between the sole and
  // the instep, heel to ball.
  nearRegion(
    "plantar-intrinsics",
    "M 92 502 C 100 498 118 496 132 498 C 136 502 136 507 132 508 C 118 510 100 509 92 507 C 89 506 89 503 92 502 Z",
  ),
];
